import type { ConditionGroup, SectionDef, TemplateDefinition } from '../forms/types.js';
import { ACCESS_STATUS_OPTIONS, NO_PASSWORDS_NOTICE, SectionBuilder } from './builder.js';
import { buildC01Section, C01_CONFIRMATION } from './c01.js';

/** F01 — SEO Optimization Onboarding (12 numbered sections + C01). */

const has = (field: string, values: string[]): ConditionGroup => ({ all: [{ field, op: 'contains_any', value: values }] });
const isTrue = (field: string): ConditionGroup => ({ all: [{ field, op: 'equals', value: true }] });

function sec(n: number, key: string, title: string) {
  const nn = String(n).padStart(2, '0');
  return new SectionBuilder(`f01_s${nn}`, { number: `F01.${nn}`, key, title });
}

function accessItem(sb: SectionBuilder, key: string, label: string, provider: string, roleHint: string) {
  sb.add(`${key}_status`, 'single_select', label, {
    options: ACCESS_STATUS_OPTIONS,
    accessProvider: provider,
    help: `Access status (Pending/Granted). Appropriate role: ${roleHint}.`,
  });
  sb.add(`${key}_invitee`, 'email', `${label}: Access Email/Invitee`, {
    accessProvider: provider,
    requiredWhen: { all: [{ field: sb.id(`${key}_status`), op: 'equals', value: sb.optionId(`${key}_status`, 'granted') }] },
  });
  sb.add(`${key}_role`, 'short_text', `${label}: Appropriate Role`, { accessProvider: provider, placeholder: roleHint });
}

function assetItem(sb: SectionBuilder, key: string, label: string, category: string, sensitivity?: 'confidential' | 'restricted') {
  const extra = { assetCategory: category, ...(sensitivity ? { sensitivity } : {}) };
  sb.add(`${key}_provided`, 'yes_no', label, { ...extra, help: 'Provided? (Yes/No)' });
  const vis = isTrue(sb.id(`${key}_provided`));
  sb.add(`${key}_file`, 'file_upload', `${label}: File`, { ...extra, visibleWhen: vis });
  sb.add(`${key}_link`, 'asset_link', `${label}: Link`, { ...extra, visibleWhen: vis });
}

function s01(): SectionDef {
  const sb = sec(1, 'business_company', 'Business / Company Information');
  sb.add('company_brand', 'short_text', 'Company/Brand', { required: true, profileKey: 'public_brand' });
  sb.add('legal_name', 'short_text', 'Legal Name', { profileKey: 'legal_name' });
  sb.add('primary_contact', 'short_text', 'Primary Contact', { required: true, profileKey: 'primary_contact_name' });
  sb.add('designation', 'short_text', 'Designation', { profileKey: 'primary_contact_designation' });
  sb.add('email', 'email', 'Email', { required: true, profileKey: 'email' });
  sb.add('phone', 'phone', 'Phone/WhatsApp', { required: true, profileKey: 'phone' });
  sb.add('industry', 'short_text', 'Industry/Niche', { required: true, profileKey: 'industry' });
  sb.add('year_established', 'year', 'Year Established', { profileKey: 'year_established' });
  sb.add('business_description', 'long_text', 'Business Description', { required: true, profileKey: 'business_description' });
  sb.add('usp', 'long_text', 'USP/Key Differentiators');
  return sb.build();
}

function s02(): SectionDef {
  const sb = sec(2, 'website', 'Website Information');
  sb.add('website_url', 'url', 'Website URL', { required: true, profileKey: 'website' });
  sb.add('cms_platform', 'short_text', 'CMS/Platform');
  sb.add('developer_agency', 'short_text', 'Developer/Agency');
  sb.add('hosting_provider', 'short_text', 'Hosting Provider');
  sb.add('cdn_waf', 'short_text', 'CDN/WAF');
  sb.add('staging_url', 'url', 'Staging URL');
  sb.add('important_sections', 'long_text', 'Important Website Sections/URLs');
  sb.add('planned_changes', 'multi_select', 'Planned Website Changes', {
    options: [
      { label: 'Redesign planned', key: 'redesign' },
      { label: 'Migration planned', key: 'migration' },
      { label: 'New pages planned', key: 'new_pages' },
      { label: 'No major changes', key: 'no_major_changes', exclusive: true },
    ],
  });
  const changed = has(sb.id('planned_changes'), ['redesign', 'migration', 'new_pages'].map((k) => sb.optionId('planned_changes', k)));
  sb.add('planned_change_details', 'long_text', 'Planned Change Details', { visibleWhen: changed, requiredWhen: changed });
  return sb.build();
}

function s03(): SectionDef {
  const sb = sec(3, 'goals', 'SEO Goals & Objectives');
  sb.add('seo_goals', 'multi_select', 'SEO Goals', {
    required: true,
    options: [
      'Organic traffic',
      'Leads',
      { label: 'Sales/revenue', key: 'sales_revenue' },
      'Rankings',
      'Brand visibility',
      'Local visibility',
      { label: 'Other', key: 'other', opensDetail: true },
    ],
  });
  sb.add('primary_objective', 'short_text', 'Primary SEO Objective', { required: true });
  sb.add('kpis_targets', 'long_text', 'KPIs/Targets');
  sb.add('expected_outcomes', 'long_text', 'Expected Outcomes/Timeline');
  return sb.build();
}

function s04(): SectionDef {
  const sb = sec(4, 'products_services_target', 'Products, Services & Target Market');
  sb.add('target_audience', 'long_text', 'Ideal Customer/Target Audience', { required: true });
  sb.add('target_locations', 'long_text', 'Target Countries/States/Cities');
  const r = 'products_services';
  sb.add(r, 'repeater', 'Products/Services', {
    rowLabel: 'Product/Service',
    required: true,
    validation: { minRows: 1, maxRows: 100 },
    children: [
      sb.child(r, 'name', 'short_text', 'Product/Service', { required: true }),
      sb.child(r, 'priority', 'single_select', 'Priority', { options: ['High', 'Medium', 'Low'] }),
      sb.child(r, 'target_location', 'short_text', 'Target Location'),
      sb.child(r, 'landing_page_url', 'url', 'Landing Page URL'),
    ],
  });
  return sb.build();
}

function s05(): SectionDef {
  const sb = sec(5, 'keywords_competitors', 'Keywords & Competitors');
  sb.add('existing_keywords', 'long_text', 'Existing Keyword Research/Target Keywords');
  sb.add('high_priority_keywords', 'long_text', 'High-Priority Keywords');
  const r = 'competitors';
  sb.add(r, 'repeater', 'Competitors', {
    rowLabel: 'Competitor',
    validation: { maxRows: 50 },
    children: [
      sb.child(r, 'competitor', 'short_text', 'Competitor', { required: true }),
      sb.child(r, 'website_url', 'url', 'Website URL'),
      sb.child(r, 'competing_offering', 'short_text', 'Competing Product/Service'),
    ],
  });
  return sb.build();
}

function s06(): SectionDef {
  const sb = sec(6, 'existing_seo', 'Existing / Previous SEO');
  sb.add('seo_handled_by', 'multi_select', 'Who handled SEO previously?', {
    options: [
      { label: 'In-house', key: 'in_house' },
      { label: 'Previous agency', key: 'previous_agency' },
      'Freelancer',
      { label: 'No previous SEO', key: 'no_previous_seo', exclusive: true },
      { label: 'Not sure', key: 'not_sure', exclusive: true },
    ],
  });
  const opt = (k: string) => sb.optionId('seo_handled_by', k);
  const hadSeo = has(sb.id('seo_handled_by'), [opt('in_house'), opt('previous_agency'), opt('freelancer')]);
  sb.add('agency_consultant_name', 'short_text', 'Agency/Consultant Name', {
    visibleWhen: has(sb.id('seo_handled_by'), [opt('previous_agency'), opt('freelancer')]),
  });
  sb.add('seo_duration', 'short_text', 'Duration', { visibleWhen: hadSeo });
  sb.add('areas_worked', 'multi_select', 'Areas Previously Worked On', {
    visibleWhen: hadSeo,
    options: [
      { label: 'Technical SEO', key: 'technical_seo' },
      { label: 'On-Page SEO', key: 'on_page_seo' },
      { label: 'Content SEO', key: 'content_seo' },
      { label: 'Local SEO', key: 'local_seo' },
      { label: 'Link Building', key: 'link_building' },
      { label: 'Digital PR', key: 'digital_pr' },
    ],
  });
  sb.add('reports_available', 'long_text', 'Reports/audits/strategy available');
  sb.add('reports_link', 'asset_link', 'Reports/Audits Link', { assetCategory: 'reports' });
  sb.add('traffic_drops', 'long_text', 'Traffic/ranking drops, penalties or issues');
  return sb.build();
}

function s07(): SectionDef {
  const sb = sec(7, 'technical_seo', 'Technical SEO');
  sb.add('technical_areas', 'multi_select', 'Technical SEO Areas', {
    options: [
      { label: 'Crawl/indexation', key: 'crawl_indexation' },
      'Sitemap',
      'Robots',
      'Canonicalization',
      { label: 'CWV', key: 'cwv' },
      'Redirects',
      'Schema',
      { label: 'JS SEO', key: 'js_seo' },
      'Mobile',
      { label: 'International/hreflang', key: 'international_hreflang' },
      { label: 'HTTPS', key: 'https' },
      'Log analysis',
    ],
  });
  sb.add('known_technical_issues', 'long_text', 'Known issues');
  sb.add('developer_requirements', 'long_text', 'Developer requirements');
  return sb.build();
}

function s08(): SectionDef {
  const sb = sec(8, 'on_page_seo', 'On-Page SEO');
  sb.add('onpage_areas', 'multi_select', 'On-Page SEO Areas', {
    options: [
      'Titles',
      'Meta descriptions',
      'Headings',
      { label: 'URLs', key: 'urls' },
      'Internal links',
      'Image SEO',
      'Alt text',
      'Schema',
      'Content optimization',
      { label: 'E-E-A-T/trust', key: 'eeat_trust' },
      'Landing page optimization',
    ],
  });
  sb.add('priority_pages', 'long_text', 'Priority pages');
  sb.add('restrictions_approvals', 'long_text', 'Restrictions/approvals');
  return sb.build();
}

function s09(): SectionDef {
  const sb = sec(9, 'off_page_seo', 'Off-Page SEO');
  sb.add('offpage_areas', 'multi_select', 'Off-Page SEO Areas', {
    options: [
      'Backlink audit',
      'Link acquisition',
      { label: 'Digital PR', key: 'digital_pr' },
      'Guest contributions',
      'Citations',
      'Brand mentions',
      'Competitor backlink analysis',
    ],
  });
  sb.add('existing_backlink_data', 'long_text', 'Existing data');
  sb.add('partnerships', 'long_text', 'Partnerships/publishers');
  sb.add('tactics_to_avoid', 'long_text', 'Tactics to avoid');
  return sb.build();
}

function s10(): SectionDef {
  const sb = sec(10, 'content', 'Content');
  sb.add('content_types', 'multi_select', 'Content Types', {
    options: [
      'Blogs',
      'Service pages',
      'Product pages',
      'Location pages',
      { label: 'FAQs', key: 'faqs' },
      'Guides',
      'Case studies',
      'Thought leadership',
    ],
  });
  sb.add('tone_style', 'long_text', 'Tone/style');
  sb.add('target_avoid_topics', 'long_text', 'Target/avoid topics');
  sb.add('approval_workflow', 'long_text', 'Approval workflow');
  return sb.build();
}

function s11(): SectionDef {
  const sb = sec(11, 'brand_assets', 'Brand & Assets');
  assetItem(sb, 'brand_guidelines', 'Brand guidelines', 'brand_guidelines');
  assetItem(sb, 'logo', 'Logo', 'logo');
  assetItem(sb, 'colors', 'Colors', 'colors');
  assetItem(sb, 'fonts', 'Fonts', 'fonts');
  assetItem(sb, 'imagery', 'Approved imagery', 'imagery');
  assetItem(sb, 'product_info', 'Product info', 'product_info');
  assetItem(sb, 'legal', 'Legal/compliance', 'legal_documents', 'confidential');
  sb.add('brand_kit', 'asset_link', 'Brand Kit/Asset Folder', { assetCategory: 'brand_kit' });
  return sb.build();
}

function s12(): SectionDef {
  const sb = sec(12, 'access_reporting', 'Access & Reporting');
  sb.info('no_passwords', NO_PASSWORDS_NOTICE, 'Access instructions');
  accessItem(sb, 'gsc', 'GSC appropriate user', 'gsc', 'Full or Restricted user');
  accessItem(sb, 'ga4', 'GA4 appropriate user', 'ga4', 'Viewer/Analyst');
  accessItem(sb, 'gtm', 'GTM appropriate user', 'gtm', 'Read/Edit as required');
  accessItem(sb, 'cms', 'CMS/Website Admin/Editor as required', 'cms', 'Admin or Editor as required');
  accessItem(sb, 'hosting_cdn', 'Hosting/CDN only if technically required', 'hosting_cdn', 'Least-privilege technical role');
  sb.info(
    'granted_declaration',
    '“Granted” is a declaration by the client. Access is verified by the team before it is relied upon.',
    'Access verification',
  );
  sb.add('primary_approval_person', 'short_text', 'Primary Approval Person', { required: true });
  sb.add('developer_contact', 'short_text', 'Developer/Technical Contact');
  sb.add('reporting_frequency', 'single_select', 'Reporting frequency', {
    required: true,
    options: ['Weekly', { label: 'Bi-weekly', key: 'biweekly' }, 'Monthly', { label: 'Custom', key: 'custom', opensDetail: true }],
  });
  sb.add('reporting_kpis', 'long_text', 'KPIs');
  sb.add('dashboard', 'short_text', 'Dashboard');
  sb.add('additional_notes', 'long_text', 'Additional notes');
  return sb.build();
}

export const F01_SEO: TemplateDefinition = {
  key: 'seo_onboarding',
  code: 'F01',
  name: 'SEO Optimization Onboarding',
  description: 'Onboarding questionnaire for SEO optimization engagements.',
  serviceModule: 'seo',
  settings: { biweeklyMeaning: 'every_two_weeks' },
  sections: [s01(), s02(), s03(), s04(), s05(), s06(), s07(), s08(), s09(), s10(), s11(), s12(), buildC01Section('f01')],
  confirmation: C01_CONFIRMATION,
};
